import {
  DotSeparatedList,
  ListItem,
  ListItemIcon,
  ListItemText,
  Text,
} from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { parseISO } from "date-fns";
import { ReactNode } from "react";

import { TimeRangeLabel } from "../DateTime/TimeRangeLabel";
import { formatDurationWithBreakParts } from "../utils/formatDurationWithBreakParts";
import { type TimeSlot } from "./Open/types";
import { iconTypeByTimeSlotFilled } from "./TimeSlotIcon";

interface ShiftDurationListItemProps {
  shiftTimeSlot: TimeSlot;
  shiftStartIso: string;
  shiftEndIso: string;
  shiftDurationInHours: number;
  breakDurationInMinutes?: number;
  requiresBreak?: boolean;
  workplaceTimeZone?: string;
  workerTimeZone?: string;
  divider?: boolean;
  minutesElapsed?: number;
  /**
   * "detailed" renders "8h (incl. 30min unpaid break)".
   * "overview" renders "8 hr total · 30 min unpaid".
   */
  displayVariant?: "detailed" | "overview";
  highlights?: ReactNode[];
}

export function ShiftDurationListItem(props: ShiftDurationListItemProps) {
  const {
    shiftTimeSlot,
    shiftStartIso,
    shiftEndIso,
    shiftDurationInHours,
    breakDurationInMinutes = 0,
    requiresBreak = false,
    workplaceTimeZone,
    workerTimeZone,
    divider = true,
    minutesElapsed = 0,
    displayVariant = "detailed",
    highlights = [],
  } = props;

  const startDate = parseISO(shiftStartIso);

  const { duration, breakInfo } = formatDurationWithBreakParts({
    totalDurationInHours: shiftDurationInHours,
    breakDurationInMinutes,
    requiresBreak,
    minutesElapsed,
    variant: displayVariant,
  });

  return (
    <ListItem divider={divider}>
      <ListItemIcon iconType={iconTypeByTimeSlotFilled[shiftTimeSlot]} />
      <ListItemText
        secondaryTypographyProps={{ component: "div" }}
        primary={
          <TimeRangeLabel
            semibold
            variant="h6"
            dateRange={{
              startDate,
              endDate: parseISO(shiftEndIso),
            }}
            timezone={workplaceTimeZone}
            deviceTimezone={workerTimeZone}
            useNowForStartDate={minutesElapsed > 0}
          />
        }
        secondary={
          <Stack direction="column" gap={5} sx={{ marginTop: 2 }}>
            {displayVariant === "overview" ? (
              <DotSeparatedList>
                <Text variant="body2">{duration}</Text>
                {breakInfo !== "" && <Text variant="body2">{breakInfo}</Text>}
              </DotSeparatedList>
            ) : (
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Text variant="body1">{duration}</Text>
                <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
                  {breakInfo}
                </Text>
              </Stack>
            )}
            {...highlights}
          </Stack>
        }
      />
    </ListItem>
  );
}
